import { useMemo } from 'react';
import cls from 'classnames';
import { RequireAtLeastOne } from 'type-fest';
import { v4 as uuidv4 } from 'uuid';

import { shortyString } from '@/common/helpers/shortyString';
import { User } from '@/user/entity/User';

import styles from './Avatar.module.css';

interface AvatarBaseProps {
  username?: Nullable<string>;
  user?: Nullable<User>;
  color?: string;
  title?: string;
  withUsername?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  online?: boolean;
  'aria-label'?: string;
}

type AvatarProps = RequireAtLeastOne<AvatarBaseProps, 'username' | 'user'>;

function Avatar(props: AvatarProps) {
  const {
    username,
    user,
    withUsername,
    size = 'medium',
    className,
    color,
    title = '',
    online = false,
    'aria-label': ariaLabel = 'User avatar',
  } = props;

  const id = useMemo(() => uuidv4(), []);
  const company = user?.b2bTitle;
  const email = user?.email;
  const name = username || user?.username;

  if (!name) {
    return null;
  }

  const avatarImage = (
    <div
      aria-label={ariaLabel}
      className={cls(
        styles.avatar,
        {
          [styles.avatarSmall]: size === 'small',
          [styles.avatarMedium]: size === 'medium',
          [styles.avatarLarge]: size === 'large',
        },
        className,
      )}
      title={title}
    >
      <svg height="100" style={{ fill: color }} viewBox="0 0 100 100" width="100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" mask={`url(#right-cut-${id})`} r="50" />

        <mask id={`right-cut-${id}`}>
          <rect fill="white" height="100" width="100" x="0" y="0" />

          <circle cx="-20" cy="50" fill="black" r="50" />
        </mask>
      </svg>

      <span className={styles.avatarFirstLetter}>{name.charAt(0).toUpperCase()}</span>

      {online && <div className={styles.avatarOnlineBadge} />}
    </div>
  );

  if (!withUsername) {
    return avatarImage;
  }

  return (
    <div className={cls(styles.avatarContainer, className)}>
      <div className={styles.avatarImageContainer}>{avatarImage}</div>

      <div className={styles.avatarName}>
        <div aria-label="Company name" className={styles.companyName}>{company ? shortyString(company, 30) : user?.getFullName}</div>

        <div aria-label="User email" className={styles.userName}>{email || name}</div>
      </div>
    </div>
  );
}

export type { AvatarProps };
export default Avatar;
